import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ShieldCheck, Loader2, Download, FileText, Search } from "lucide-react";
import { useTableSort } from "@/lib/tableSort";
import { MerkezHistory } from "@/components/MerkezHistory";
import VoterEditDialog from "@/components/VoterEditDialog";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export const MerkezApproval = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");
  const [sel, setSel] = useState([]);
  const [view, setView] = useState("pending");
  const [search, setSearch] = useState("");
  const [editId, setEditId] = useState(null);
  const mkT = useTableSort([]);

  const load = () => api.get("/yetki/merkez-pending")
    .then(({ data }) => { setData(data); setSel([]); })
    .catch(() => setData({ rows: [], total: 0 }));
  useEffect(() => { load(); }, []);

  const notifyChange = () => window.dispatchEvent(new Event("merkez-pending-changed"));

  const approve = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/yetki/merkez-pending/${r.id}/approve`);
      toast.success("Onaylandı — belge artık Seçim Ofisinde kayıtlı");
      notifyChange(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onaylanamadı");
    } finally { setBusy(null); }
  };

  const bulkApprove = async () => {
    setBusy("bulk");
    try {
      const { data: res } = await api.post("/yetki/merkez-pending/bulk-approve", { ids: sel });
      toast.success(`${res.count} belge onaylandı`);
      notifyChange(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onaylanamadı");
    } finally { setBusy(null); }
  };

  const doReject = async () => {
    if (reason.trim().length < 3) { toast.error("Ret sebebini kısaca yazın"); return; }
    setBusy("bulk");
    try {
      if (reject === "bulk") {
        const { data: res } = await api.post("/yetki/merkez-pending/bulk-reject", { ids: sel, reason: reason.trim() });
        toast.success(`${res.count} belge reddedildi`);
      } else {
        await api.post(`/yetki/merkez-pending/${reject.id}/reject`, { reason: reason.trim() });
        toast.success("Reddedildi — belge yeri temizlendi");
      }
      setReject(null); setReason("");
      notifyChange(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem yapılamadı");
    } finally { setBusy(null); }
  };

  if (!data) return <Skeleton className="h-40 w-full" />;
  const canApprove = !!data.can_approve;
  const q = search.trim().toLocaleLowerCase("tr");
  const filtered = q
    ? (data.rows || []).filter((r) => [r.company_name, r.sicil_no, r.committee_name,
        r.voting_person, r.merkez_pending_by, r.committee_no ? `#${r.committee_no}` : ""]
        .some((x) => (x || "").toString().toLocaleLowerCase("tr").includes(q)))
    : (data.rows || []);
  const rows = mkT.applySort(filtered);
  const allSel = rows.length > 0 && rows.every((r) => sel.includes(r.id));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const doExport = async (fmt) => {
    setBusy("export");
    try {
      const res = await api.get("/yetki/merkez-pending/export", { params: { format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `secim_ofisi_belge_onayi.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-3" data-testid="merkez-approval">
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldCheck className="h-4 w-4 text-amber-600" />
        <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="merkez-view-tabs">
          <button type="button" onClick={() => setView("pending")}
            className={`px-3 py-1.5 text-xs font-medium ${view === "pending" ? "bg-amber-600 text-white" : "hover:bg-muted"}`}
            data-testid="merkez-view-pending">Bekleyenler ({data.total})</button>
          <button type="button" onClick={() => setView("history")}
            className={`px-3 py-1.5 text-xs font-medium ${view === "history" ? "bg-[#0F2C59] text-white" : "hover:bg-muted"}`}
            data-testid="merkez-view-history">Geçmiş</button>
        </div>
        {!canApprove && (
          <span className="text-xs text-muted-foreground">— onaylama yetkiniz yok, yalnız görüntülüyorsunuz</span>
        )}
        {view === "pending" && (
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8" disabled={!rows.length || busy === "export"}
              onClick={() => doExport("xlsx")} data-testid="merkez-export-xlsx">
              <Download className="h-3.5 w-3.5 mr-1.5" />Excel
            </Button>
            <Button variant="outline" size="sm" className="h-8" disabled={!rows.length || busy === "export"}
              onClick={() => doExport("pdf")} data-testid="merkez-export-pdf">
              <FileText className="h-3.5 w-3.5 mr-1.5" />PDF
            </Button>
          </div>
        )}
      </div>

      {view === "history" ? <MerkezHistory /> : (<>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9 h-9" placeholder="Firma, sicil no, komite, yetkili veya işaretleyen ara..."
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="merkez-search" />
        </div>
        <span className="text-xs text-muted-foreground" data-testid="merkez-count">
          {q ? <><b>{rows.length}</b> / {data.total} belge</> : <><b>{data.total}</b> belge onay bekliyor</>}
        </span>
        {q && (
          <button type="button" className="text-xs underline text-muted-foreground hover:text-foreground"
            onClick={() => setSearch("")} data-testid="merkez-search-clear">aramayı temizle</button>
        )}
      </div>

      {canApprove && sel.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 p-2.5" data-testid="merkez-bulk-bar">
          <span className="text-sm font-medium">{sel.length} belge seçildi</span>
          <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" disabled={busy === "bulk"}
            onClick={bulkApprove} data-testid="merkez-bulk-approve">
            {busy === "bulk" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Seçilenleri Onayla</>}
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-red-600" disabled={busy === "bulk"}
            onClick={() => { setReject("bulk"); setReason(""); }} data-testid="merkez-bulk-reject">
            <XCircle className="h-3.5 w-3.5 mr-1" />Seçilenleri Reddet
          </Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setSel([])} data-testid="merkez-bulk-clear">
            Seçimi temizle
          </Button>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="merkez-empty">
          {q ? `"${search}" için onay bekleyen belge bulunamadı.` : "Onay bekleyen belge yok."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm" data-testid="merkez-table">
            <thead className="bg-muted/60">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                {canApprove && (
                  <th className="px-3 py-2 w-10">
                    <Checkbox checked={allSel} onCheckedChange={(v) => setSel(v ? rows.map((r) => r.id) : [])}
                      data-testid="merkez-select-all" />
                  </th>
                )}
                <mkT.Th className="px-3 py-2 font-medium" sortKey="company" text get={(r) => r.company_name}>Firma</mkT.Th>
                <mkT.Th className="px-3 py-2 font-medium" sortKey="committee" lowBest get={(r) => r.committee_no}>Komite</mkT.Th>
                <mkT.Th className="px-3 py-2 font-medium" sortKey="person" text get={(r) => r.voting_person}>Oy Kullanacak Yetkili</mkT.Th>
                <mkT.Th className="px-3 py-2 font-medium" sortKey="by" text get={(r) => r.merkez_pending_by}>İşaretleyen</mkT.Th>
                <mkT.Th className="px-3 py-2 font-medium" sortKey="date" get={(r) => r.merkez_pending_at}>Tarih</mkT.Th>
                <th className="px-3 py-2 font-medium text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/60" data-testid={`merkez-row-${r.id}`}>
                  {canApprove && (
                    <td className="px-3 py-2">
                      <Checkbox checked={sel.includes(r.id)} onCheckedChange={() => toggle(r.id)}
                        data-testid={`merkez-select-${r.id}`} />
                    </td>
                  )}
                  <td className="px-3 py-2 font-medium">
                    <button type="button" onClick={() => setEditId(r.id)}
                      data-testid={`merkez-open-${r.id}`}
                      className="text-left hover:underline text-[#0F2C59] dark:text-sky-300">
                      {r.company_name}
                    </button>
                    {r.sicil_no && <span className="text-muted-foreground font-normal"> · {r.sicil_no}</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="text-muted-foreground">#{r.committee_no}</span> {r.committee_name}
                  </td>
                  <td className="px-3 py-2">{r.voting_person || <span className="text-amber-600">belirlenmemiş</span>}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.merkez_pending_by || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmt(r.merkez_pending_at)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canApprove ? (
                      <div className="inline-flex gap-1.5">
                        <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" disabled={!!busy}
                          onClick={() => approve(r)} data-testid={`merkez-approve-${r.id}`}>
                          {busy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Onayla</>}
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-red-600" disabled={!!busy}
                          onClick={() => { setReject(r); setReason(""); }} data-testid={`merkez-reject-${r.id}`}>
                          <XCircle className="h-3.5 w-3.5 mr-1" />Reddet
                        </Button>
                      </div>
                    ) : <span className="text-xs text-muted-foreground">bekliyor</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        “Seçim Ofisine Dilekçesi Verildi” işaretlenen firmalar burada bekler; onaylanana kadar yetki belgesi alınmış sayılmaz
        ve hiçbir sayaca girmez. Onaylandığında belge “Seçim Ofisi” olarak kayıtlara geçer.
      </p>
      </>)}

      <VoterEditDialog voterId={editId} open={!!editId} onClose={() => setEditId(null)}
        onSaved={() => { notifyChange(); load(); }} />

      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent className="max-w-md" data-testid="merkez-reject-dialog">
          <DialogHeader><DialogTitle className="text-base">Seçim Ofisi onayını reddet</DialogTitle></DialogHeader>
          {reject && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {reject === "bulk" ? `${sel.length} belge — sebep hepsine uygulanır` : reject.company_name}
              </p>
              <Label className="text-xs">Ret sebebi (işaretleyen kullanıcı için)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Örn. Ofiste böyle bir dilekçe bulunamadı" data-testid="merkez-reject-reason" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReject(null)} data-testid="merkez-reject-cancel">Vazgeç</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={doReject} disabled={!!busy} data-testid="merkez-reject-confirm">
              Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MerkezApproval;
